import type { LeaderGroupNode } from '@/modules/companies'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay, parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import styled, { useTheme } from 'styled-components'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './components/DietButton'
import { useWorkPeriodApprovePermissions } from './hooks'
import { WorkPeriodAddons } from './components/work-period-addons/work-period-addons'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useSalaryApproveColumns } from './hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'
import { SalaryDayButton } from './components/SalaryDayButton'
import { Button, Combobox, ConditionalWrapper, H2, Tooltip } from '@/components'
import {
  Skeleton,
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import { useState } from 'react'
import { format } from '@/utils/date-fns'
import { WorkPeriodHoursAndMap } from './WorkPeriodHoursAndMap'
import truncate from 'lodash/truncate'
import type { ApprovalRow } from './approval-rows'
import { CalendarItemCommentsButton } from './CalendarItemCommentsButton'
import { useWorkPeriodApprovalActions } from './useWorkPeriodApprovalActions'
import { useWorkPeriodDayExtensionActions } from './use-work-period-day-extension-actions'
import { getWorkPeriodDayExtensionState } from './work-period-day-extension'
import { GRAPH_WORK_PERIOD, WORK_PERIOD } from './queries'
import { ViewWorkPeriodLogsModal } from './components/view-work-period-logs-modal'
import type { WorkPeriodFlag } from './components/work-period-flags'
import { WorkPeriodFlags } from './components/work-period-flags'
import { RestBandRow, RestPeriodRow } from './components/rest-row/rest-rows'
import { WorkPeriodAllocation } from './components/work-period-allocation'
import { enforceHourMinuteString } from '@/modules/activities/util'

const CommentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
`

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`

const EmptyCommentAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  font-style: italic;
`

const CommentCellInner = styled.div<{ clickable?: boolean }>`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  min-height: 1.5rem;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`

const ApproverName = styled.div`
  margin-top: 0.15rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.75rem;
  line-height: 1.2;
`

const ApprovalIconButton = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`

const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const WeekHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const BulkApproveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`

// One tbody per work period, so a period and its expanded detail stay one
// block. The separator lives here rather than on the rows for the same reason:
// the line belongs between periods, not between a period and its own detail.
const WorkPeriodBody = styled.tbody<{
  $color: string | null
  $isOpen: boolean
}>`
  &:not(:first-of-type) {
    border-top: 1px solid ${props => props.theme.colors.gray8};
  }

  && > tr:first-child,
  && > tr:first-child > td {
    background-color: ${props => props.$color ?? 'transparent'};
  }

  ${props =>
    props.$isOpen &&
    `
      && > tr:nth-child(2),
      && > tr:nth-child(2) > td {
        background-color: ${props.theme.colors.gray11};
      }
    `}

  &&:hover > tr,
  &&:hover > tr > td {
    background-color: ${props => props.theme.colors.gray10};
  }
`

const ActionCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`

const ManualHoursTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 22rem;
`

const ManualHoursCommentList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0;
  padding-left: 1rem;
`

const ManualHoursComment = styled.li`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const ManualHoursTime = styled.span`
  font-weight: 600;
`

const UserCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 12rem;
  max-width: 100%;
  min-width: 0;
`

const UserName = styled.div`
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
`

const UserDateLine = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
`

const TimeCellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 9rem;
  max-width: 100%;
`

const PlannedTime = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.75rem;
  line-height: 1.2;
`

const ExtensionNote = styled.div`
  color: ${props => props.theme.colors.primary500};
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
`

const ActualTime = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
`

const LocationDetail = styled.div`
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EstimatedBadge = styled.span`
  align-self: flex-start;
  margin-top: 0.15rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.4rem;
  background: ${props => props.theme.colors.orange};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

export const WorkPeriodApproveTable: React.FC<{
  approvalRows: ApprovalRow[]
  leaderGroups: LeaderGroupNode[]
  showUserColumn?: boolean
  singleUserSelected?: boolean
  loading: boolean
  initRefetch: () => void
  refetchAfterApproval: () => void
}> = ({
  approvalRows,
  leaderGroups,
  loading,
  showUserColumn,
  singleUserSelected,
  initRefetch,
  refetchAfterApproval,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    start: 'common.start',
    end: 'common.end',
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    estimated: 'salary.estimated',
    comment: 'common.comment',
    managerComment: 'common.manager-comment',
    planned: 'common.planned',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    salaryMode: 'common.salary-mode',
    leaderGroup: 'common.leader-group',
    chooseGroup: 'common.choose-group',
    addons: 'salary.addons',
    workHours: 'common.work-hours',
    pause: 'common.pause',
    department: 'common.department',
    facility: 'common.facility',
    departmentCount: ['common.department-expandable-text', { amount: 0 }],
    facilityCount: ['common.facility-expandable-text', { amount: 0 }],
    transferedOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
    markIndividualActivitiesAsNotPaid:
      'salary.mark-individual-activities-as-not-paid',
    theSumShowsTotalWorkBeforeManuallyAddedHours:
      'salary.the-sum-shows-total-work-before-manually-added-hours',
    manualHours: 'salary.this-work-day-has-manual-hours-from-time-entries',
    ferryUsed: 'salary.work-day-includes-ferry',
    unknownVehicle: 'salary.work-day-includes-unknown-vehicle',
    onlyBlankVehicleDriving: 'salary.work-day-only-blank-vehicle-driving',
    lockedByManager: 'salary.locked-by-manager-approval',
    noPermissionToChange: 'salary.no-permission-to-change',
    driverMustConfirmBeforeApproval:
      'salary.driver-must-confirm-before-approval',
    commentRequiredDriverApproval: 'salary.comment-required-on-driver-approval',
    commentRequiredManagerApproval:
      'salary.comment-required-on-manager-approval',
    placeholder: 'common.standard-for-user',
    extendWorkPeriod: 'salary.extend-work-period',
    addedBefore: 'salary.extend-work-period-added-before',
    addedAfter: 'salary.extend-work-period-added-after',
    actionLog: 'salary.work-period-log',
    editedAfterLock: 'salary.edited-after-lock',
    editedAfterLockAt: ['salary.edited-after-lock-at', { date: '' }],
    approveAllForWeek: 'salary.approve-all-for-week',
    approveAllForMonth: 'salary.approve-all-for-month',
    approveAllConfirm: ['salary.approve-all-confirm', { n: 0 }],
  })

  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()

  const openLogs = (workPeriodId: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriodId} onClose={resolve} />
    ))
  const [openWorkPeriods, setOpenWorkPeriods] = useState<string[]>([])
  const [showMaps, setShowMaps] = useState(true)

  const salaryConfiguration = useSalaryModuleConfiguration()
  const { isEnabled: isColumnEnabled, getDisplayName } =
    useSalaryApproveColumns()
  const showSalaryMode = isColumnEnabled(ColumnNames.SALARY_NAME)
  const showEmployeeGroup = isColumnEnabled(ColumnNames.EMPLOYEE_GROUP)
  const showDiet = isColumnEnabled(ColumnNames.DIET)
  const showAddons = isColumnEnabled(ColumnNames.ADDONS)
  const showWorkHours = isColumnEnabled(ColumnNames.TOTAL_WORK)
  const showPause = isColumnEnabled(ColumnNames.PAUSE)
  const showDepartment = isColumnEnabled(ColumnNames.DEPARTMENT)
  const showFacility = isColumnEnabled(ColumnNames.FACILITY)
  // Driver/manager: workflow option ON → always show (capability OR toggle)
  // Flex: feature option OFF → always hide (capability AND toggle)
  const showApprovedByDriver =
    salaryConfiguration.driverApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_DRIVER)
  const showApprovedByManager =
    salaryConfiguration.managerApprove ||
    isColumnEnabled(ColumnNames.APPROVED_BY_MANAGER)
  const showTransferredToFlex =
    salaryConfiguration.canTransferWorkToAbsence &&
    isColumnEnabled(ColumnNames.TRANSFERRED_TO_FLEX)

  // The identity cell spans two columns, so four fixed cells precede the
  // configurable ones. Full-width rows (expanded detail, rest bands) span all.
  const numColumns =
    4 +
    (showWorkHours ? 1 : 0) +
    (showPause ? 1 : 0) +
    (showSalaryMode ? 1 : 0) +
    (showEmployeeGroup ? 1 : 0) +
    (showDepartment ? 1 : 0) +
    (showFacility ? 1 : 0) +
    (showDiet ? 1 : 0) +
    (showAddons ? 1 : 0) +
    (showApprovedByDriver ? 1 : 0) +
    (showApprovedByManager ? 1 : 0) +
    (showTransferredToFlex ? 1 : 0)

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()
  const { hasPermissions } = usePermissions()
  const canAddTimeEntry = hasPermissions(PERMISSIONS.timesheets.add.timeentry)

  const { transferOvertimeToFlex, patchWorkPeriod } = useWorkPeriodMigrations()
  const {
    openApprovalCommentFlow,
    toggleApproval,
    approveManyAsManager,
    isBulkApproving,
  } = useWorkPeriodApprovalActions({
    refetchAfterApproval,
    patchWorkPeriod,
    salaryConfiguration,
    commentRequiredDriverApproval: translations.commentRequiredDriverApproval,
    commentRequiredManagerApproval: translations.commentRequiredManagerApproval,
  })
  const { openExtendDayFlow, creatingExtension } =
    useWorkPeriodDayExtensionActions({
      initRefetch,
    })

  // Group work periods by week
  const approvalRowGroups = approvalRows.reduce(
    (acc, row) => {
      const weekStart = getWeek(parseISO(row.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(row)

      return acc
    },
    {} as Record<string, ApprovalRow[]>
  )

  // Work periods the current manager may still approve (driver-confirmed where
  // required, not yet manager-approved). Feeds the "approve all" bulk actions.
  const getManagerApprovable = (rows: ApprovalRow[]) =>
    rows
      .map(row => row.workPeriod)
      .filter((workPeriod): workPeriod is NonNullable<typeof workPeriod> =>
        Boolean(workPeriod)
      )
      .filter(workPeriod => {
        const { canApproveAsManager } = workPeriodApprovalState(
          me.id,
          workPeriod
        )
        return canApproveAsManager && !workPeriod.approvedByManager
      })

  // Only offer bulk approval to a manager who has drilled into a single driver.
  const showBulkApprove = Boolean(singleUserSelected) && showApprovedByManager
  const monthApprovable = showBulkApprove
    ? getManagerApprovable(approvalRows)
    : []

  function computeColWidths(
    workPeriodGroup: ApprovalRow[],
    showUserColumn?: boolean
  ) {
    const hasUserComments = workPeriodGroup.some(
      row => !!row.workPeriod?.userComment?.trim()
    )
    const hasManagerComments = workPeriodGroup.some(
      row => !!row.workPeriod?.managerComment?.trim()
    )
    const userCommentColWidth = hasUserComments
      ? showUserColumn
        ? '17%'
        : '20%'
      : showUserColumn
      ? '8%'
      : '10%'

    const managerCommentColWidth = hasManagerComments
      ? showUserColumn
        ? '17%'
        : '20%'
      : showUserColumn
      ? '8%'
      : '10%'

    const startStopColWidth =
      !hasUserComments && !hasManagerComments
        ? showUserColumn
          ? '12%'
          : '14%'
        : showUserColumn
        ? '10%'
        : '12%'

    const userInfoColWidth = showUserColumn ? '16%' : '14%'

    return {
      startStopColWidth,
      userInfoColWidth,
      userCommentColWidth,
      managerCommentColWidth,
    }
  }

  return (
    <span>
      {showBulkApprove && monthApprovable.length > 0 && (
        <BulkApproveBar>
          <Button
            variant="outline"
            disabled={isBulkApproving}
            onClick={() => {
              void approveManyAsManager(
                monthApprovable,
                translations.approveAllConfirm({ n: monthApprovable.length })
              )
            }}
          >
            {translations.approveAllForMonth}
          </Button>
        </BulkApproveBar>
      )}
      {loading && approvalRows.length === 0 && (
        <div className="flex flex-col gap-3 rounded-xl bg-white p-4 shadow-card">
          {[0, 1, 2].map(row => (
            <Skeleton key={row} className="h-16 w-full rounded-md" />
          ))}
        </div>
      )}
      {Object.entries(approvalRowGroups).map(group => {
        const weekNumber = group[0]
        const approvalRowGroup = group[1]

        const {
          startStopColWidth,
          userInfoColWidth,
          userCommentColWidth,
          managerCommentColWidth,
        } = computeColWidths(approvalRowGroup, showUserColumn)

        const weekApprovable = showBulkApprove
          ? getManagerApprovable(approvalRowGroup)
          : []

        return (
          <GroupWrapper key={weekNumber}>
            <WeekHeader>
              <H2>{translations.weekN({ n: weekNumber })}</H2>
              {weekApprovable.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isBulkApproving}
                  onClick={() => {
                    void approveManyAsManager(
                      weekApprovable,
                      translations.approveAllConfirm({
                        n: weekApprovable.length,
                      })
                    )
                  }}
                >
                  {translations.approveAllForWeek}
                </Button>
              )}
            </WeekHeader>
            {/* No side padding: a row's own background is the approval
                state, so it has to reach the card's edges rather than sit on a
                white margin. The header cells round their outer corners to the
                card's, which only lines up without it. */}
            <div className="overflow-hidden rounded-xl bg-white shadow-card">
              <Table containerClassName="overflow-x-auto">
                <colgroup>
                  <col style={{ width: '12%' }} />
                  <col style={{ width: userInfoColWidth }} />
                  <col style={{ width: startStopColWidth }} />
                  <col style={{ width: startStopColWidth }} />
                  {showWorkHours && <col style={{ width: '7%' }} />}
                  {showPause && <col style={{ width: '6%' }} />}
                  {showSalaryMode && <col style={{ width: '10%' }} />}
                  {showEmployeeGroup && <col style={{ width: '12%' }} />}
                  {showDepartment && <col style={{ width: '10%' }} />}
                  {showFacility && <col style={{ width: '10%' }} />}
                  {showDiet && <col style={{ width: '10%' }} />}
                  {showAddons && <col style={{ width: '8%' }} />}
                  {showApprovedByDriver && (
                    <col style={{ width: userCommentColWidth }} />
                  )}
                  {showApprovedByManager && (
                    <col style={{ width: managerCommentColWidth }} />
                  )}
                  {showTransferredToFlex && <col style={{ width: '8%' }} />}
                </colgroup>

                <TableHeader>
                  <TableRow>
                    <TableHead colSpan={2}>
                      {showUserColumn ? translations.user : translations.date}
                    </TableHead>
                    <TableHead>{translations.start}</TableHead>
                    <TableHead>{translations.end}</TableHead>
                    {showWorkHours && (
                      <TableHead>
                        {getDisplayName(ColumnNames.TOTAL_WORK) ||
                          translations.workHours}
                      </TableHead>
                    )}
                    {showPause && (
                      <TableHead>
                        {getDisplayName(ColumnNames.PAUSE) ||
                          translations.pause}
                      </TableHead>
                    )}
                    {showSalaryMode && (
                      <TableHead>
                        {getDisplayName(ColumnNames.SALARY_NAME) ||
                          translations.salaryMode}
                      </TableHead>
                    )}
                    {showEmployeeGroup && (
                      <TableHead>
                        {getDisplayName(ColumnNames.EMPLOYEE_GROUP) ||
                          translations.leaderGroup}
                      </TableHead>
                    )}
                    {showDepartment && (
                      <TableHead>
                        {getDisplayName(ColumnNames.DEPARTMENT) ||
                          translations.department}
                      </TableHead>
                    )}
                    {showFacility && (
                      <TableHead>
                        {getDisplayName(ColumnNames.FACILITY) ||
                          translations.facility}
                      </TableHead>
                    )}
                    {showDiet && (
                      <TableHead>
                        {getDisplayName(ColumnNames.DIET) || translations.diet}
                      </TableHead>
                    )}
                    {showAddons && (
                      <TableHead>
                        {getDisplayName(ColumnNames.ADDONS) ||
                          translations.addons}
                      </TableHead>
                    )}
                    {showApprovedByDriver && (
                      <TableHead>
                        {getDisplayName(ColumnNames.APPROVED_BY_DRIVER) ||
                          translations.approvedByUser}
                      </TableHead>
                    )}
                    {showApprovedByManager && (
                      <TableHead>
                        {getDisplayName(ColumnNames.APPROVED_BY_MANAGER) ||
                          translations.approvedByManager}
                      </TableHead>
                    )}
                    {showTransferredToFlex && (
                      <TableHead>
                        {getDisplayName(ColumnNames.TRANSFERRED_TO_FLEX) ||
                          translations.transferedOvertimeToFlextime}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                {approvalRowGroup.map(row => {
                  if (row.type === 'calendarItem') {
                    const plannedStart = parseISO(row.calendarItem.start)
                    const plannedEnd = parseISO(row.calendarItem.end)
                    const plannedDateLines = isSameDay(plannedStart, plannedEnd)
                      ? [format(plannedStart, 'EEE dd. MMMM')]
                      : [
                          format(plannedStart, 'EEE dd. MMMM'),
                          format(plannedEnd, 'EEE dd. MMMM'),
                        ]
                    return (
                      <WorkPeriodBody
                        key={row.id}
                        $color="white"
                        $isOpen={false}
                      >
                        <TableRow>
                          <TableCell>
                            <CalendarItemCommentsButton
                              calendarItem={row.calendarItem}
                            />
                          </TableCell>
                          <TableCell>
                            <UserCellContent>
                              {showUserColumn && (
                                <UserName>{row.user.fullName}</UserName>
                              )}
                              {plannedDateLines.map(dateLine => (
                                <UserDateLine key={dateLine}>
                                  {dateLine}
                                </UserDateLine>
                              ))}
                              <LocationDetail>
                                {row.calendarItem.title}
                              </LocationDetail>
                            </UserCellContent>
                          </TableCell>
                          <TableCell narrow>
                            <TimeCellContent>
                              <PlannedTime>{translations.planned}</PlannedTime>
                              <ActualTime>
                                {format(plannedStart, 'HH:mm')}
                              </ActualTime>
                            </TimeCellContent>
                          </TableCell>
                          <TableCell narrow>
                            <TimeCellContent>
                              <PlannedTime>{translations.planned}</PlannedTime>
                              <ActualTime>
                                {format(plannedEnd, 'HH:mm')}
                              </ActualTime>
                            </TimeCellContent>
                          </TableCell>
                          {showWorkHours && <TableCell />}
                          {showPause && <TableCell />}
                          {showSalaryMode && <TableCell />}
                          {showEmployeeGroup && <TableCell />}
                          {showDepartment && <TableCell />}
                          {showFacility && <TableCell />}
                          {showDiet && <TableCell />}
                          {showAddons && <TableCell />}
                          {showApprovedByDriver && <TableCell />}
                          {showApprovedByManager && <TableCell />}
                          {showTransferredToFlex && <TableCell />}
                        </TableRow>
                      </WorkPeriodBody>
                    )
                  }

                  if (row.type === 'restPeriod') {
                    // A daily rest is the seam between two shifts; only the
                    // weekly ones carry compensation and earn a row.
                    return row.restPeriod.isWeekRest ? (
                      <RestPeriodRow
                        key={row.id}
                        restPeriod={row.restPeriod}
                        userId={row.user.id}
                        canEdit={
                          !row.restPeriod.workPeriodBefore.approvedByManager
                        }
                        showWorkHours={showWorkHours}
                        showPause={showPause}
                        showSalaryMode={showSalaryMode}
                        showEmployeeGroup={showEmployeeGroup}
                        showDepartment={showDepartment}
                        showFacility={showFacility}
                        showDiet={showDiet}
                        showAddons={showAddons}
                        showApprovedByDriver={showApprovedByDriver}
                        showApprovedByManager={showApprovedByManager}
                        showTransferredToFlex={showTransferredToFlex}
                        onChange={initRefetch}
                      />
                    ) : (
                      <RestBandRow
                        key={row.id}
                        restPeriod={row.restPeriod}
                        columnCount={numColumns}
                      />
                    )
                  }

                  const workPeriod = row.workPeriod
                  const {
                    driverApproveDirty,
                    managerApproveDirty,
                    canApproveAsDriver,
                    canApproveAsManager,
                    managerBlockedByDriverConfirmation,
                  } = workPeriodApprovalState(me.id, workPeriod)
                  const driverLocked =
                    !canApproveAsDriver && workPeriod.approvedByManager
                  const managerBlockedReason =
                    managerBlockedByDriverConfirmation
                      ? translations.driverMustConfirmBeforeApproval
                      : translations.noPermissionToChange

                  let color: string | null =
                    !salaryConfiguration.driverApprove &&
                    !salaryConfiguration.managerApprove
                      ? null
                      : 'white'

                  if (
                    !workPeriod.approvedByUser &&
                    salaryConfiguration.driverApprove &&
                    workPeriod.userComment
                  ) {
                    color = theme.colors.red100
                  }
                  if (
                    !workPeriod.approvedByManager &&
                    salaryConfiguration.managerApprove &&
                    workPeriod.managerComment
                  ) {
                    color = theme.colors.red100
                  }

                  if (workPeriod.approvedByManager) {
                    color = theme.colors.green100
                  }
                  // Edited after lock takes precedence — flag the row orange.
                  // Opaque tint (not rgba): the row background is applied to both
                  // the tr and each td, so a translucent value would stack and
                  // render unevenly.
                  if (workPeriod.editedAfterLock) {
                    color = '#ffeccc'
                  }
                  const isOpen = openWorkPeriods.includes(workPeriod.id)
                  const leaderGroupItems = [
                    ...leaderGroups.map(group => ({
                      value: group.id,
                      label: group.name,
                    })),
                  ]
                  if (
                    workPeriod.leaderGroup &&
                    !leaderGroupItems.some(
                      item => item.value === workPeriod.leaderGroup?.id
                    )
                  ) {
                    leaderGroupItems.unshift({
                      value: workPeriod.leaderGroup.id,
                      label: workPeriod.leaderGroup.name,
                    })
                  }

                  const workPeriodStart = parseISO(workPeriod.datetimeStart)
                  const workPeriodEnd = parseISO(workPeriod.datetimeEnd)
                  const extensionState = getWorkPeriodDayExtensionState({
                    workPeriodStart,
                    workPeriodEnd,
                    timeEntries: workPeriod.timeEntries,
                  })
                  const plannedStart = workPeriod.calendarItem?.start
                    ? format(parseISO(workPeriod.calendarItem.start), 'HH:mm')
                    : null
                  const plannedEnd = workPeriod.calendarItem?.end
                    ? format(parseISO(workPeriod.calendarItem.end), 'HH:mm')
                    : null
                  const workPeriodDateLines = isSameDay(
                    workPeriodStart,
                    workPeriodEnd
                  )
                    ? [format(workPeriodStart, 'EEE dd. MMMM')]
                    : [
                        format(workPeriodStart, 'EEE dd. MMMM'),
                        format(workPeriodEnd, 'EEE dd. MMMM'),
                      ]
                  const startLocation = shortenLocationDetail(
                    workPeriod.startLocationDetail
                  )
                  const endLocation = shortenLocationDetail(
                    workPeriod.endLocationDetail
                  )
                  const canToggleFlexTransfer =
                    !!workPeriod.absenceHourTransaction ||
                    (salaryConfiguration.canTransferWorkToAbsence &&
                      !!workPeriod.salarySetting?.useDayOvertime &&
                      !workPeriod.salarySetting.useWeekOvertime)
                  const canExtendDay =
                    canAddTimeEntry &&
                    !workPeriod.approvedByManager &&
                    !!workPeriod.user.salaryWorkTime &&
                    (canApproveAsDriver || canApproveAsManager)
                  const timeEntriesWithComments = workPeriod.timeEntries.filter(
                    entry => entry.comment.trim()
                  )
                  const flags: WorkPeriodFlag[] = []
                  if (workPeriod.editedAfterLock) {
                    flags.push({
                      key: 'edited-after-lock',
                      icon: 'exclamation-triangle',
                      severity: 'high',
                      label: workPeriod.lockedAt
                        ? translations.editedAfterLockAt({
                            date: format(
                              parseISO(workPeriod.lockedAt),
                              'dd.MM.yyyy HH:mm'
                            ),
                          })
                        : translations.editedAfterLock,
                    })
                  }
                  if (workPeriod.hasTimeEntries) {
                    flags.push({
                      key: 'manual-hours',
                      icon: 'pen-to-square',
                      severity: 'medium',
                      label: (
                        <ManualHoursTooltip>
                          <div>{translations.manualHours}</div>
                          {timeEntriesWithComments.length > 0 && (
                            <ManualHoursCommentList>
                              {timeEntriesWithComments.map(entry => (
                                <ManualHoursComment key={entry.id}>
                                  <ManualHoursTime>
                                    {format(
                                      parseISO(entry.datetimeStart),
                                      'HH:mm'
                                    )}
                                    {entry.datetimeEnd
                                      ? ` - ${format(
                                          parseISO(entry.datetimeEnd),
                                          'HH:mm'
                                        )}`
                                      : ''}
                                    {': '}
                                  </ManualHoursTime>
                                  {entry.comment}
                                </ManualHoursComment>
                              ))}
                            </ManualHoursCommentList>
                          )}
                        </ManualHoursTooltip>
                      ),
                    })
                  }
                  if (workPeriod.hasUnknownVehicle) {
                    flags.push({
                      key: 'unknown-vehicle',
                      icon: 'truck',
                      severity: 'high',
                      label: translations.unknownVehicle,
                    })
                  }
                  if (workPeriod.hasOnlyBlankVehicleDriving) {
                    flags.push({
                      key: 'only-blank-vehicle-driving',
                      icon: 'truck-front',
                      severity: 'medium',
                      label: translations.onlyBlankVehicleDriving,
                    })
                  }
                  if (workPeriod.hasFerryConditions) {
                    flags.push({
                      key: 'ferry',
                      icon: 'ferry',
                      severity: 'low',
                      label: translations.ferryUsed,
                    })
                  }

                  return (
                    <WorkPeriodBody
                      key={workPeriod.id}
                      $color={color}
                      $isOpen={workPeriod.salarySetting ? isOpen : false}
                    >
                      <TableRow>
                        <TableCell>
                          <ActionCellContent>
                            <WorkPeriodFlags flags={flags} />
                            {workPeriod.salarySetting && (
                              <Button
                                size="icon-lg"
                                variant="outline"
                                onClick={() => {
                                  if (isOpen) {
                                    setOpenWorkPeriods(
                                      openWorkPeriods.filter(
                                        id => id !== workPeriod.id
                                      )
                                    )
                                  } else {
                                    setOpenWorkPeriods([
                                      ...openWorkPeriods,
                                      workPeriod.id,
                                    ])
                                  }
                                }}
                              >
                                <Icon
                                  icon="clock"
                                  color={theme.colors.primary}
                                  size="30"
                                />
                              </Button>
                            )}
                            {canExtendDay && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={creatingExtension}
                                onClick={() => {
                                  void openExtendDayFlow(workPeriod)
                                }}
                              >
                                {translations.extendWorkPeriod}
                              </Button>
                            )}
                            {workPeriod.calendarItem && (
                              <CalendarItemCommentsButton
                                calendarItem={workPeriod.calendarItem}
                              />
                            )}
                            <Button
                              size="icon-lg"
                              variant="outline"
                              title={translations.actionLog}
                              aria-label={translations.actionLog}
                              onClick={() => openLogs(workPeriod.id)}
                            >
                              <Icon
                                icon="clock-rotate-left"
                                color={theme.colors.primary}
                                size="24"
                              />
                            </Button>
                          </ActionCellContent>
                        </TableCell>
                        <TableCell>
                          <UserCellContent>
                            {showUserColumn && (
                              <UserName>{workPeriod.user.fullName}</UserName>
                            )}
                            {workPeriodDateLines.map(dateLine => (
                              <UserDateLine key={dateLine}>
                                {dateLine}
                              </UserDateLine>
                            ))}
                            {workPeriod.isEstimated && (
                              <EstimatedBadge>
                                {translations.estimated}
                              </EstimatedBadge>
                            )}
                          </UserCellContent>
                        </TableCell>
                        <TableCell narrow>
                          <TimeCellContent>
                            {plannedStart && (
                              <PlannedTime>
                                {translations.planned} {plannedStart}
                              </PlannedTime>
                            )}
                            <ActualTime>
                              {format(workPeriodStart, 'HH:mm')}
                            </ActualTime>
                            {extensionState.beforeExtensionStart && (
                              <ExtensionNote>
                                {translations.addedBefore}{' '}
                                {format(
                                  extensionState.beforeExtensionStart,
                                  'HH:mm'
                                )}
                              </ExtensionNote>
                            )}
                            {startLocation && (
                              <LocationDetail>{startLocation}</LocationDetail>
                            )}
                          </TimeCellContent>
                        </TableCell>
                        <TableCell narrow>
                          <TimeCellContent>
                            {plannedEnd && (
                              <PlannedTime>
                                {translations.planned} {plannedEnd}
                              </PlannedTime>
                            )}
                            <ActualTime>
                              {format(workPeriodEnd, 'HH:mm')}
                            </ActualTime>
                            {extensionState.afterExtensionEnd && (
                              <ExtensionNote>
                                {translations.addedAfter}{' '}
                                {format(
                                  extensionState.afterExtensionEnd,
                                  'HH:mm'
                                )}
                              </ExtensionNote>
                            )}
                            {endLocation && (
                              <LocationDetail>{endLocation}</LocationDetail>
                            )}
                          </TimeCellContent>
                        </TableCell>
                        {showWorkHours && (
                          <TableCell narrow className="font-mono">
                            {enforceHourMinuteString(
                              workPeriod.activityTimes?.timeWorkTotal
                            )}
                          </TableCell>
                        )}
                        {showPause && (
                          <TableCell narrow className="font-mono">
                            {enforceHourMinuteString(
                              workPeriod.activityTimes?.timePause
                            )}
                          </TableCell>
                        )}
                        {showSalaryMode && (
                          <TableCell narrow>
                            <SalarySettingSelect
                              value={workPeriod.salarySetting?.id || null}
                              disabled={workPeriod.approvedByManager}
                              placeholder={translations.placeholder}
                              onChange={async value => {
                                if (workPeriod.approvedByManager) return
                                await patchWorkPeriod({
                                  variables: {
                                    id: workPeriod.id,
                                    input: {
                                      salarySetting:
                                        value === '' ? null : value,
                                    },
                                  },
                                })
                                initRefetch()
                              }}
                            />
                            {workPeriod.salarySetting?.salaryType == 'DAY' && (
                              <SalaryDayButton
                                workPeriod={workPeriod}
                                salaryDay={workPeriod.salaryDay}
                                userId={workPeriod.user.id}
                                canEdit={
                                  !workPeriod.approvedByManager &&
                                  (canApproveAsManager ||
                                    !workPeriod.approvedByUser)
                                }
                                border={true}
                                onChange={() => initRefetch()}
                                restrictWidth
                              />
                            )}
                          </TableCell>
                        )}
                        {showEmployeeGroup && (
                          <TableCell narrow>
                            {(leaderGroupItems.length > 0 ||
                              workPeriod.leaderGroup) && (
                              <Combobox
                                mode="single"
                                value={workPeriod.leaderGroup?.id ?? ''}
                                items={leaderGroupItems}
                                clearable
                                className="min-w-[180px]"
                                disabled={workPeriod.approvedByManager}
                                placeholder={translations.chooseGroup}
                                onChange={async value => {
                                  if (workPeriod.approvedByManager) return
                                  await patchWorkPeriod({
                                    variables: {
                                      id: workPeriod.id,
                                      input: {
                                        leaderGroup: value || null,
                                      },
                                    },
                                  })
                                  initRefetch()
                                }}
                              />
                            )}
                          </TableCell>
                        )}
                        {showDepartment && (
                          <TableCell narrow>
                            <WorkPeriodAllocation
                              title={translations.department}
                              items={(workPeriod.departments ?? []).map(
                                ({ department, duration }) => ({
                                  ...department,
                                  duration,
                                })
                              )}
                              countLabel={amount =>
                                translations.departmentCount({ amount })
                              }
                              datetimeStart={workPeriodStart}
                              datetimeEnd={workPeriodEnd}
                            />
                          </TableCell>
                        )}
                        {showFacility && (
                          <TableCell narrow>
                            <WorkPeriodAllocation
                              title={translations.facility}
                              items={(workPeriod.facilities ?? []).map(
                                ({ facility, duration }) => ({
                                  ...facility,
                                  duration,
                                })
                              )}
                              countLabel={amount =>
                                translations.facilityCount({ amount })
                              }
                              datetimeStart={workPeriodStart}
                              datetimeEnd={workPeriodEnd}
                            />
                          </TableCell>
                        )}
                        {showDiet && (
                          <TableCell narrow>
                            <DietButton
                              workPeriod={workPeriod}
                              userId={workPeriod.user.id}
                              canEdit={!workPeriod.approvedByManager}
                              border={true}
                              onChange={() => initRefetch()}
                            />
                          </TableCell>
                        )}
                        {showAddons && (
                          <TableCell onClick={event => event.stopPropagation()}>
                            <WorkPeriodAddons
                              userId={workPeriod.user.id}
                              datetimeStart={workPeriodStart}
                              datetimeEnd={workPeriodEnd}
                              addonsAsText={workPeriod.addonsAsText}
                              canEdit={!workPeriod.approvedByManager}
                              lockedLabel={translations.lockedByManager}
                              onChange={() => initRefetch()}
                            />
                          </TableCell>
                        )}
                        {/* User comment / approval */}
                        {showApprovedByDriver && (
                          <TableCell
                            narrow
                            className={cn(
                              canApproveAsDriver && 'cursor-pointer',
                              driverLocked && 'cursor-not-allowed opacity-50'
                            )}
                          >
                            <CommentRow>
                              <ConditionalWrapper
                                condition={driverLocked}
                                wrapper={c => (
                                  <Tooltip
                                    content={translations.lockedByManager}
                                  >
                                    {c}
                                  </Tooltip>
                                )}
                              >
                                <ApprovalIconButton
                                  disabled={!canApproveAsDriver}
                                  onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    if (!canApproveAsDriver) return
                                    void toggleApproval(workPeriod, 'driver')
                                  }}
                                >
                                  <Checkmark
                                    checked={workPeriod.approvedByUser}
                                    icon={
                                      !driverApproveDirty
                                        ? driverLocked
                                          ? 'lock'
                                          : 'plus-circle'
                                        : undefined
                                    }
                                    color={
                                      !driverApproveDirty
                                        ? theme.colors.gray3
                                        : undefined
                                    }
                                  />
                                </ApprovalIconButton>
                              </ConditionalWrapper>
                              <CommentCellInner
                                clickable={canApproveAsDriver}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsDriver) return
                                  void openApprovalCommentFlow(
                                    workPeriod,
                                    'driver'
                                  )
                                }}
                              >
                                {workPeriod.userComment && (
                                  <CommentText>
                                    {workPeriod.userComment}
                                  </CommentText>
                                )}
                                {!workPeriod.userComment &&
                                  canApproveAsDriver && (
                                    <EmptyCommentAction>
                                      <Icon icon="comment" />
                                      {translations.comment}
                                    </EmptyCommentAction>
                                  )}
                                {workPeriod.approvedByUser &&
                                  workPeriod.userApprover && (
                                    <ApproverName>
                                      {workPeriod.userApprover.fullName}
                                    </ApproverName>
                                  )}
                              </CommentCellInner>
                            </CommentRow>
                          </TableCell>
                        )}

                        {/* Manager comment / approval */}
                        {showApprovedByManager && (
                          <TableCell
                            narrow
                            className={cn(
                              canApproveAsManager
                                ? 'cursor-pointer'
                                : 'cursor-not-allowed opacity-50'
                            )}
                          >
                            <CommentRow>
                              <ConditionalWrapper
                                condition={!canApproveAsManager}
                                wrapper={c => (
                                  <Tooltip content={managerBlockedReason}>
                                    {c}
                                  </Tooltip>
                                )}
                              >
                                <ApprovalIconButton
                                  disabled={!canApproveAsManager}
                                  onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    if (!canApproveAsManager) return
                                    void toggleApproval(workPeriod, 'manager')
                                  }}
                                >
                                  <Checkmark
                                    checked={workPeriod.approvedByManager}
                                    icon={
                                      !managerApproveDirty
                                        ? !canApproveAsManager
                                          ? 'lock'
                                          : 'plus-circle'
                                        : undefined
                                    }
                                    color={
                                      !managerApproveDirty
                                        ? theme.colors.gray3
                                        : undefined
                                    }
                                  />
                                </ApprovalIconButton>
                              </ConditionalWrapper>
                              <CommentCellInner
                                clickable={canApproveAsManager}
                                onClick={event => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  if (!canApproveAsManager) return
                                  void openApprovalCommentFlow(
                                    workPeriod,
                                    'manager'
                                  )
                                }}
                              >
                                {workPeriod.managerComment && (
                                  <CommentText>
                                    {workPeriod.managerComment}
                                  </CommentText>
                                )}
                                {!workPeriod.managerComment &&
                                  canApproveAsManager && (
                                    <EmptyCommentAction>
                                      <Icon icon="comment" />
                                      {translations.comment}
                                    </EmptyCommentAction>
                                  )}
                                {workPeriod.approvedByManager &&
                                  workPeriod.managerApprover && (
                                    <ApproverName>
                                      {workPeriod.managerApprover.fullName}
                                    </ApproverName>
                                  )}
                              </CommentCellInner>
                            </CommentRow>
                          </TableCell>
                        )}
                        {showTransferredToFlex && (
                          <TableCell
                            narrow
                            center
                            className={cn(
                              canToggleFlexTransfer
                                ? 'cursor-pointer'
                                : 'cursor-default'
                            )}
                            onClick={() => {
                              if (!canToggleFlexTransfer) return
                              void (async () => {
                                const result = await transferOvertimeToFlex({
                                  variables: { workPeriod: workPeriod.id },
                                  refetchQueries: [
                                    {
                                      query: WORK_PERIOD,
                                      variables: { id: workPeriod.id },
                                    },
                                    {
                                      query: GRAPH_WORK_PERIOD,
                                      variables: { id: workPeriod.id },
                                    },
                                  ],
                                  awaitRefetchQueries: true,
                                })
                                if (
                                  result.data
                                    ?.transferOvertimeWorkToAbsenceHourTransaction
                                    .success
                                ) {
                                  await initRefetch()
                                }
                              })()
                            }}
                          >
                            <Icon
                              icon={
                                workPeriod.absenceHourTransaction
                                  ? 'check'
                                  : 'times'
                              }
                              color={
                                workPeriod.absenceHourTransaction
                                  ? theme.colors.green
                                  : undefined
                              }
                            />
                          </TableCell>
                        )}
                      </TableRow>
                      {workPeriod.salarySetting && isOpen && (
                        <TableRow>
                          <TableCell colSpan={numColumns}>
                            <WorkPeriodHoursAndMap
                              workPeriodId={workPeriod.id}
                              activitiesOnly={
                                workPeriod.salarySetting.salaryType !==
                                  'HOUR' &&
                                (workPeriod.salaryDay?.partOfDay ?? 0) > 0
                              }
                              showMap={showMaps}
                              onToggleMap={() => setShowMaps(value => !value)}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </WorkPeriodBody>
                  )
                })}
              </Table>
            </div>
          </GroupWrapper>
        )
      })}
    </span>
  )
}

type LocationDetailInfo = {
  description?: string | null
  postalCode?: string | null
  postalCity?: string | null
}

function shortenLocationDetail(location?: LocationDetailInfo | null) {
  if (!location) return null

  const description = truncate(
    location.description
      ?.split(',')
      .map(part => part.trim())
      .filter(Boolean)[0] ?? '',
    { length: 25 }
  )
  const place = [location.postalCode, location.postalCity]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ')

  return [description, place].filter(Boolean).join(', ') || null
}
